import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LogOut } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import '@/styles/dashboard.css';
import '@/styles/photo-editor.css';

import * as api from '@/lib/officeApi';
import { cycleReportPatch } from '@/components/office/helpers';
import WorksTab from '@/components/office/WorksTab';
import NewWorkModal from '@/components/office/NewWorkModal';
import OnHoldReasonModal from '@/components/office/OnHoldReasonModal';
import FeeModal from '@/components/office/FeeModal';
import WorkDetail from '@/components/office/WorkDetail';
import StaffTab from '@/components/office/StaffTab';
import { StaffModal, PinModal } from '@/components/office/StaffModal';
import InspectionModal from '@/components/office/InspectionModal';
import ClosedTab from '@/components/office/ClosedTab';
import FinanceTab from '@/components/office/FinanceTab';
import ContactsTab from '@/components/office/ContactsTab';
import ContactModal from '@/components/office/ContactModal';

const TABS = [
  { id: 'works', label: 'Works' },
  { id: 'staff', label: 'Staff' },
  { id: 'finance', label: 'Finance' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'closed', label: 'Closed' },
];

function ConfirmModal({ open, title, body, danger, confirmLabel, onConfirm, onClose, testid }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 340 }} data-testid={testid}>
        <div className="modal-header"><h3 style={danger ? { color: '#be123c' } : {}}>{title}</h3><button className="modal-close" onClick={onClose}>&times;</button></div>
        <p style={{ fontSize: 14, color: '#57534e', marginBottom: 16 }}>{body}</p>
        <div className="modal-actions"><button className="btn-outline" onClick={onClose}>Cancel</button><button className="btn-primary" style={danger ? { background: '#be123c' } : {}} onClick={onConfirm} data-testid={`${testid}-confirm`}>{confirmLabel}</button></div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const [tab, setTab] = useState('works');
  const [reports, setReports] = useState([]);
  const [staff, setStaff] = useState([]);
  const [dailyExpenses, setDailyExpenses] = useState([]);
  const [officeExpenses, setOfficeExpenses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [detailWork, setDetailWork] = useState(null);
  const [workModalOpen, setWorkModalOpen] = useState(false);
  const [editingWork, setEditingWork] = useState(null);
  const [reasonWork, setReasonWork] = useState(null);
  const [feeWork, setFeeWork] = useState(null);
  const [deleteWork, setDeleteWork] = useState(null);
  const [moveWork, setMoveWork] = useState(null);
  const [reopenWork, setReopenWork] = useState(null);
  const [viewShot, setViewShot] = useState(null);
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffDelete, setStaffDelete] = useState(null);
  const [pinUser, setPinUser] = useState(null);
  const [resetPinUser, setResetPinUser] = useState(null);
  const [inspWork, setInspWork] = useState(null);
  const [undoInsp, setUndoInsp] = useState(null);
  const [confirmComplete, setConfirmComplete] = useState(null);
  const [confirmRevert, setConfirmRevert] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [prefillContact, setPrefillContact] = useState(null);
  const [defaultContactCat, setDefaultContactCat] = useState(null);
  const [deleteContact, setDeleteContact] = useState(null);

  const staffById = Object.fromEntries(staff.map((s) => [s.user_id, s]));

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [r, u, de, oe, ct] = await Promise.all([api.getReports(), isAdmin ? api.getUsers() : Promise.resolve([]), api.getDailyExpenses(), api.getOfficeExpenses(), api.getContacts()]);
        if (!active) return;
        setReports(r); setStaff(u); setDailyExpenses(de); setOfficeExpenses(oe); setContacts(ct);
      } catch (e) { toast.error('Load failed: ' + e.message); } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const doLogout = async () => { await logout(); navigate('/login'); };

  const patchReport = async (id, patch) => {
    const prev = reports;
    setReports((cur) => cur.map((w) => (w.id === id ? { ...w, ...patch } : w)));
    setDetailWork((d) => (d && d.id === id ? { ...d, ...patch } : d));
    try { const up = await api.updateReport(id, patch); setReports((cur) => cur.map((w) => (w.id === id ? up : w))); setDetailWork((d) => (d && d.id === id ? up : d)); }
    catch (e) { setReports(prev); toast.error('Update failed: ' + e.message); }
  };

  const handleSubmitWork = async ({ mode, id, data, list }) => {
    try {
      if (mode === 'edit') { const up = await api.updateReport(id, data); setReports((c) => c.map((w) => (w.id === id ? up : w))); setDetailWork((d) => (d && d.id === id ? up : d)); toast.success('Work updated'); }
      else { const created = await api.createReportsBulk(list); setReports((c) => [...created, ...c]); toast.success(`${created.length} ${created.length === 1 ? 'work' : 'works'} created`); }
      setWorkModalOpen(false); setEditingWork(null);
    } catch (e) { toast.error('Save failed: ' + e.message); }
  };

  const handleDeleteWork = async (w) => {
    setDeleteWork(null); setDetailWork(null);
    const prev = reports; setReports((c) => c.filter((x) => x.id !== w.id));
    try { await api.deleteReport(w.id); toast.success('Work deleted'); } catch (e) { setReports(prev); toast.error('Delete failed'); }
  };

  const toggleInsp = (r) => {
    // Not Completed → open inspection modal to capture inspector/GPS/photos
    // Completed → ask for confirmation before reverting
    if (!r.inspection_done) setInspWork(r);
    else setUndoInsp(r);
  };
  const confirmUndoInsp = async () => {
    const r = undoInsp; setUndoInsp(null);
    await patchReport(r.id, { inspection_done: false, report_done: false, collected: false });
    toast.success('Inspection reverted to Not Completed');
  };
  const cycleReport = (r) => {
    // State 1 (NotCompleted) → State 2 (OnHold): silent, no reason yet, card stays in Active
    if (!r.report_done && !r.collected && !r.is_on_hold) {
      patchReport(r.id, { is_on_hold: true, report_done: false, collected: false });
      return;
    }
    // State 2 (OnHold) → State 3 (Completed): CONFIRMATION required
    if (r.is_on_hold) { setConfirmComplete(r); return; }
    // State 3 (Completed, !collected) → State 4 (Collected): silent
    // GATE: block if balance > 0 AND !pay_confirmed (admin must verify in Finance / Bank Credit tab first)
    if (r.report_done && !r.collected) {
      const bal = Math.max(0, Number(r.fees || 0) - Number(r.discount || 0) - Number(r.advance || 0));
      if (bal > 0 && !r.pay_confirmed) {
        toast('Clear the balance fee in Finance tab first (admin only)', {
          style: { background: '#1c1917', color: '#fef3c7', border: 'none' },
          duration: 3500,
        });
        return;
      }
      patchReport(r.id, { collected: true, collected_date: new Date().toISOString() });
      return;
    }
    // State 4 (Collected) → State 1 (NotCompleted): CONFIRMATION required (revert)
    setConfirmRevert(r);
  };
  const confirmCompleteAction = async () => {
    const r = confirmComplete; setConfirmComplete(null);
    // Backend auto-generates file_ref on first report_done=true (kept on subsequent revert/redo)
    await patchReport(r.id, { is_on_hold: false, hold_reason: null, hold_notes: null, report_done: true, collected: false, collected_date: null });
    toast.success('Report marked Completed');
  };
  const confirmRevertAction = async () => {
    const r = confirmRevert; setConfirmRevert(null);
    // Reset to State 1 — KEEP file_ref intact (backend never clears it once assigned)
    await patchReport(r.id, { report_done: false, collected: false, is_on_hold: false, hold_reason: null, hold_notes: null,
      collected_date: null, report_completed_date: null });
    toast.success('Moved back to Active');
  };
  const saveReason = async (r, reason, notes) => {
    if (!reason) { setReasonWork(null); return; }
    // Standalone "+ Add reason" flow — work is already on_hold, this just files the reason + optional notes
    await patchReport(r.id, { is_on_hold: true, hold_reason: reason, hold_notes: notes || null });
    setReasonWork(null);
  };
  const saveFee = async (r, patch) => { await patchReport(r.id, patch); setFeeWork(null); toast.success('Fee saved'); };
  const confirmMove = async () => { const r = moveWork; setMoveWork(null); await patchReport(r.id, { archived: true, archived_at: new Date().toISOString() }); toast.success('Moved to Closed'); };
  const confirmReopen = async () => { const r = reopenWork; setReopenWork(null); await patchReport(r.id, { archived: false, archived_at: null, collected: false, report_done: false }); toast.success('Reopened'); };

  // staff
  const handleSaveStaff = async ({ mode, id, data }) => {
    try {
      if (mode === 'edit') { const u = await api.updateUser(id, data); setStaff((c) => c.map((s) => (s.user_id === id ? u : s))); toast.success('Staff updated'); }
      else { const u = await api.createUser(data); setStaff((c) => [...c, u]); toast.success('Staff added'); }
      setStaffModalOpen(false); setEditingStaff(null);
    } catch (e) { toast.error('Failed: ' + e.message); }
  };
  const confirmStaffDelete = async () => { const s = staffDelete; setStaffDelete(null); const prev = staff; setStaff((c) => c.filter((x) => x.user_id !== s.user_id)); try { await api.deleteUser(s.user_id); toast.success('Staff removed'); } catch (e) { setStaff(prev); toast.error('Failed'); } };
  const savePin = async (u, pin) => { await api.setPin(u.user_id, pin); setStaff((c) => c.map((s) => (s.user_id === u.user_id ? { ...s, has_pin: true } : s))); setPinUser(null); toast.success('PIN set'); };
  const confirmResetPin = async () => { const u = resetPinUser; setResetPinUser(null); try { await api.resetPin(u.user_id); setStaff((c) => c.map((s) => (s.user_id === u.user_id ? { ...s, has_pin: false } : s))); toast.success('PIN reset'); } catch (e) { toast.error('Failed'); } };

  // expenses
  const addDailyExp = async (b) => { try { const e = await api.createDailyExpense(b); setDailyExpenses((c) => [...c, e]); } catch (er) { toast.error('Failed'); } };
  const verifyDailyExp = async (id, v) => { setDailyExpenses((c) => c.map((e) => (e.id === id ? { ...e, verified: v } : e))); try { await api.verifyDailyExpense(id, v); } catch (er) { toast.error('Failed'); } };
  const delDailyExp = async (id) => { setDailyExpenses((c) => c.filter((e) => e.id !== id)); try { await api.deleteDailyExpense(id); } catch (er) { toast.error('Failed'); } };
  const addOfficeExp = async (b) => { try { const e = await api.createOfficeExpense(b); setOfficeExpenses((c) => [...c, e]); } catch (er) { toast.error('Failed'); } };
  const verifyOfficeExp = async (id, v) => { setOfficeExpenses((c) => c.map((e) => (e.id === id ? { ...e, verified: v } : e))); try { await api.verifyOfficeExpense(id, v); } catch (er) { toast.error('Failed'); } };
  const delOfficeExp = async (id) => { setOfficeExpenses((c) => c.filter((e) => e.id !== id)); try { await api.deleteOfficeExpense(id); } catch (er) { toast.error('Failed'); } };

  // contacts (CRM)
  const handleSaveContact = async ({ mode, id, data }) => {
    try {
      if (mode === 'edit') {
        const c = await api.updateContact(id, data);
        setContacts((cur) => cur.map((x) => (x.id === id ? c : x)));
        toast.success('Contact updated');
      } else {
        const c = await api.createContact(data);
        setContacts((cur) => [...cur, c].sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())));
        toast.success('Contact saved');
      }
      setContactModalOpen(false); setEditingContact(null); setPrefillContact(null); setDefaultContactCat(null);
    } catch (e) { toast.error('Save failed: ' + e.message); }
  };
  const handleQuickSaveContact = async ({ name, mobile, category, location }) => {
    // Auto-save from NewWorkModal row — instant toast confirmation
    const cleanName = (name || '').trim();
    if (!cleanName) { toast.error('Add a client name first'); return false; }
    if (!category) { toast.error('Pick a contact type first'); return false; }
    try {
      const payload = {
        name: cleanName, mobile: (mobile || '').trim(), email: '', category,
        bank: '', branch: '', designation: '', agency: '', relation: '',
        property_location: category === 'client' ? (location || '').trim() : '',
        notes: '',
      };
      const c = await api.createContact(payload);
      setContacts((cur) => [...cur, c].sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())));
      toast.success(`Saved ${cleanName} to Contacts`);
      return true;
    } catch (e) { toast.error('Failed: ' + e.message); return false; }
  };
  const confirmDeleteContact = async () => {
    const c = deleteContact; setDeleteContact(null);
    const prev = contacts; setContacts((cur) => cur.filter((x) => x.id !== c.id));
    try { await api.deleteContact(c.id); toast.success('Contact deleted'); }
    catch (e) { setContacts(prev); toast.error('Delete failed'); }
  };

  const worksHandlers = {
    onEdit: (r) => { setEditingWork(r); setWorkModalOpen(true); },
    onDelete: (r) => setDeleteWork(r),
    onToggleInsp: toggleInsp, onCycleReport: cycleReport,
    onOpenDetail: (r) => setDetailWork(r), onEditDocsReason: (r) => setReasonWork(r),
    onSetFee: (r) => setFeeWork(r), onMoveToClosed: (r) => setMoveWork(r),
  };

  return (
    <div className="office-dashboard" data-testid="office-dashboard">
      <Toaster position="top-right" />
      <header className="od-header">
        <div className="od-header-inner">
          <div><div className="od-title">Office Dashboard</div><div className="od-subtitle">Welcome, {user?.name || '—'}</div></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <nav className="od-nav">
              {TABS.map((t) => <button key={t.id} className={`od-tab${tab === t.id && !detailWork ? ' active' : ''}`} onClick={() => { setTab(t.id); setDetailWork(null); }} data-testid={`tab-${t.id}`}>{t.label}</button>)}
            </nav>
            <button className="od-logout" onClick={() => setLogoutConfirm(true)} data-testid="logout-btn"><LogOut style={{ width: 14, height: 14 }} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="od-container">
        {loading ? <div className="od-spinner" data-testid="loading" /> : detailWork ? (
          <WorkDetail work={detailWork} staff={staff} staffById={staffById} currentUser={user}
            onClose={() => setDetailWork(null)}
            onEdit={worksHandlers.onEdit} onDelete={(r) => setDeleteWork(r)} onSetFee={(r) => setFeeWork(r)}
            onPatch={async (w, patch) => { await patchReport(w.id, patch); }} />
        ) : (
          <>
            {tab === 'works' && <WorksTab reports={reports} staff={staff} staffById={staffById} onNewWork={() => { setEditingWork(null); setWorkModalOpen(true); }} onViewShot={setViewShot} {...worksHandlers} />}
            {tab === 'staff' && <StaffTab reports={reports} staff={staff} onAddStaff={() => { setEditingStaff(null); setStaffModalOpen(true); }} onEditStaff={(s) => { setEditingStaff(s); setStaffModalOpen(true); }} onRemoveStaff={(s) => setStaffDelete(s)} onSetPin={(u) => setPinUser(u)} onResetPin={(u) => setResetPinUser(u)} />}
            {tab === 'finance' && <FinanceTab reports={reports} staff={staff} dailyExpenses={dailyExpenses} officeExpenses={officeExpenses} onUpdateReport={patchReport} onAddDailyExpense={addDailyExp} onVerifyDailyExpense={verifyDailyExp} onDeleteDailyExpense={delDailyExp} onAddOfficeExpense={addOfficeExp} onVerifyOfficeExpense={verifyOfficeExp} onDeleteOfficeExpense={delOfficeExp} />}
            {tab === 'contacts' && <ContactsTab contacts={contacts}
              onNewContact={() => { setEditingContact(null); setPrefillContact(null); setDefaultContactCat(null); setContactModalOpen(true); }}
              onEditContact={(c) => { setEditingContact(c); setPrefillContact(null); setDefaultContactCat(null); setContactModalOpen(true); }}
              onDeleteContact={(c) => setDeleteContact(c)} />}
            {tab === 'closed' && <ClosedTab reports={reports} staffById={staffById} onReopen={(r) => setReopenWork(r)} />}
          </>
        )}
      </div>

      {workModalOpen && <NewWorkModal work={editingWork} staff={staff} contacts={contacts} onSaveContact={handleQuickSaveContact} onOpenNewContact={() => { setEditingContact(null); setPrefillContact(null); setDefaultContactCat(null); setContactModalOpen(true); }} onClose={() => { setWorkModalOpen(false); setEditingWork(null); }} onSubmit={handleSubmitWork} />}
      {contactModalOpen && <ContactModal contact={editingContact} prefill={prefillContact} defaultCategory={defaultContactCat}
        onClose={() => { setContactModalOpen(false); setEditingContact(null); setPrefillContact(null); setDefaultContactCat(null); }}
        onSave={handleSaveContact} />}
      {reasonWork && <OnHoldReasonModal work={reasonWork} onClose={() => setReasonWork(null)} onSave={saveReason} />}
      {feeWork && <FeeModal work={feeWork} staff={staff} onClose={() => setFeeWork(null)} onSave={saveFee} onViewShot={setViewShot} />}
      {staffModalOpen && <StaffModal staff={editingStaff} onClose={() => { setStaffModalOpen(false); setEditingStaff(null); }} onSubmit={handleSaveStaff} />}
      {pinUser && <PinModal user={pinUser} onClose={() => setPinUser(null)} onSave={savePin} />}
      {inspWork && <InspectionModal work={inspWork} staff={staff} currentUser={user}
        onClose={() => setInspWork(null)}
        onSave={async (w, patch) => { await patchReport(w.id, patch); setInspWork(null); toast.success('Inspection marked as Completed'); }} />}

      <ConfirmModal open={!!undoInsp} title="Revert Inspection?" body={`Are you sure you want to change "${undoInsp?.client_name}" inspection back to Not Completed? This will also reset Report Completed and Collected.`} danger confirmLabel="Yes, Revert" onConfirm={confirmUndoInsp} onClose={() => setUndoInsp(null)} testid="undo-insp-modal" />
      <ConfirmModal open={!!confirmComplete} title="Mark report as Completed?" body={`Client name: ${confirmComplete?.client_name || '—'}`} confirmLabel="Yes, Completed" onConfirm={confirmCompleteAction} onClose={() => setConfirmComplete(null)} testid="confirm-complete-modal" />
      <ConfirmModal open={!!confirmRevert} title="Move back to Active?" body={`Bring "${confirmRevert?.client_name}" back to Active? This will reset Report and Collected. The file reference will be preserved.`} confirmLabel="Yes, Move to Active" onConfirm={confirmRevertAction} onClose={() => setConfirmRevert(null)} testid="confirm-revert-modal" />

      <ConfirmModal open={!!deleteWork} title="Delete Work?" body={`Delete "${deleteWork?.client_name}"? This cannot be undone.`} danger confirmLabel="Delete" onConfirm={() => handleDeleteWork(deleteWork)} onClose={() => setDeleteWork(null)} testid="delete-work-modal" />
      <ConfirmModal open={!!deleteContact} title="Delete Contact?" body={`Delete "${deleteContact?.name}"? This cannot be undone.`} danger confirmLabel="Delete" onConfirm={confirmDeleteContact} onClose={() => setDeleteContact(null)} testid="delete-contact-modal" />
      <ConfirmModal open={!!moveWork} title="Move to Closed?" body={`Move "${moveWork?.client_name}" to the Closed tab?`} confirmLabel="Move" onConfirm={confirmMove} onClose={() => setMoveWork(null)} testid="move-modal" />
      <ConfirmModal open={!!reopenWork} title="Reopen Work?" body={`Bring "${reopenWork?.client_name}" back to Active?`} confirmLabel="Reopen" onConfirm={confirmReopen} onClose={() => setReopenWork(null)} testid="reopen-modal" />
      <ConfirmModal open={!!staffDelete} title="Remove Staff?" body={`Remove ${staffDelete?.name}? This cannot be undone.`} danger confirmLabel="Remove" onConfirm={confirmStaffDelete} onClose={() => setStaffDelete(null)} testid="staff-delete-modal" />
      <ConfirmModal open={!!resetPinUser} title="Reset PIN?" body={`Clear ${resetPinUser?.name}'s PIN? They'll set a new one on next login.`} danger confirmLabel="Reset PIN" onConfirm={confirmResetPin} onClose={() => setResetPinUser(null)} testid="reset-pin-modal" />
      <ConfirmModal open={logoutConfirm} title="Log out?" body="Are you sure you want to log out?" confirmLabel="Log Out" onConfirm={doLogout} onClose={() => setLogoutConfirm(false)} testid="logout-modal" />

      {viewShot && (
        <div className="modal-overlay" onClick={() => setViewShot(null)} data-testid="screenshot-modal">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3 style={{ fontSize: 14 }}>Payment Screenshot</h3><button className="modal-close" onClick={() => setViewShot(null)}>&times;</button></div>
            <img src={viewShot.startsWith('data:') || viewShot.startsWith('http') ? viewShot : `${process.env.REACT_APP_BACKEND_URL}/api/files/${viewShot}?auth=${encodeURIComponent(localStorage.getItem('auth_token') || '')}`} alt="payment" style={{ width: '100%', borderRadius: 6 }} />
          </div>
        </div>
      )}
    </div>
  );
}
